// constants
import { DEFAULT_CHANNEL, DEFAULT_CURRENCY, REACT_IDENTIFIER, USER_STATUS } from './constants';

// helpers
import { isDev, getReactAppType, storeTasks, clearTasks, getStoredTasks } from './helpers';
import NFA from 'shared/helpers/analytics/NFAnalytics';

// defs
import { IDataLayer, TASK_TYPES, ITask } from './types';

export default class DTM {
  private static _tasks: ITask[] = [];

  private static _taskTimer: number | undefined;
  private static _isLoginSet = false;

  private static _datalayer: IDataLayer = {
    previous_page_url: '',
    previous_page_type: '',
    previous_page: '',
    pagename: '',
    page_url: '', // Set Initial pageUrl as referrer
    user_login_status: USER_STATUS.GUEST,
    pagetype: '',
    channel: DEFAULT_CHANNEL,
    currency: DEFAULT_CURRENCY,
    ui: REACT_IDENTIFIER.BROWSER,
    phone: '',
    userEmail: '',
    firstName: '',
    lastName: '',
    eventName: '',
    customerId: '',
    AB: {},
    email: '',
    analyticsSdkData: {},
  };

  private static updateGlobalDatalayer() {
    try {
      window.datalayer = { ...DTM._datalayer, AB: window.api_ab };
    } catch (e) {
      /** DO NOTHING */
    }
  }

  private static onUnload() {
    if (DTM._tasks.length === 0) {
      return;
    }

    storeTasks(DTM._tasks);

    DTM._tasks = [];
  }

  private static onLoad() {
    const savedTasks = getStoredTasks();
    clearTasks();

    DTM._tasks = [...savedTasks, ...DTM._tasks];
    DTM._doTasks();
  }

  public static init() {
    if (window.datalayer === undefined) {
      window.datalayer = {};
    }
    window.addEventListener('beforeunload', this.onUnload);
    this.onLoad();
  }

  public static setUserData({
    phone,
    email,
    firstName,
    lastName,
    customerId,
  }: Pick<IDataLayer, 'phone' | 'email' | 'firstName' | 'lastName' | 'customerId'>) {
    DTM._datalayer.phone = phone;
    DTM._datalayer.userEmail = email;
    DTM._datalayer.firstName = firstName;
    DTM._datalayer.lastName = lastName;
    DTM._datalayer.customerId = customerId;
    DTM._isLoginSet = true;
    DTM._datalayer.user_login_status = USER_STATUS.LOGGED_IN;
    DTM.updateGlobalDatalayer();
  }

  public static set isLoggedIn(loggedIn: boolean) {
    DTM._isLoginSet = true;
    DTM._datalayer.user_login_status = loggedIn ? USER_STATUS.LOGGED_IN : USER_STATUS.GUEST;
    DTM.updateGlobalDatalayer();
  }

  public static get isLoggedIn() {
    return DTM._datalayer.user_login_status === USER_STATUS.LOGGED_IN;
  }

  public static set currency(currency: string) {
    DTM._datalayer.currency = currency;
    DTM.updateGlobalDatalayer();
  }

  public static get currency() {
    return DTM._datalayer.currency;
  }

  public static updateDatalayer(data: object = {}) {
    DTM._datalayer = {
      ...DTM._datalayer,
      ...data,
    };
    DTM.updateGlobalDatalayer();
  }

  public static getDatalayer = () => {
    return this._datalayer;
  };

  public static onPageLoad(data: Partial<IDataLayer>) {
    NFA.firePageView();
    DTM._tasks.push({
      type: TASK_TYPES.PAGE_LOAD,
      data,
    });
    DTM._doTasks();
  }

  public static track(eventName: string, data: Record<string, any> = {}) {
    DTM._tasks.push({
      type: TASK_TYPES.EVENT,
      eventName,
      data,
    });

    DTM._doTasks();
  }

  private static _doTasks = () => {
    /**
     * check if timer already exists,
     * if timer exists means the execution loop is waiting for main thread
     */
    if (DTM._taskTimer !== undefined) {
      return;
    }

    // wait for launch JS to load and login status to be set
    if (DTM._isLoginSet === false || window._satellite === undefined) {
      // @ts-ignore
      DTM._taskTimer = setTimeout(() => {
        DTM._taskTimer = undefined;
        DTM._doTasks();
      }, 500);
      return;
    }

    /**
     * Execute all DTM tasks when main thread is free
     */
    // @ts-ignore
    DTM._taskTimer = window.requestIdleCallback(() => {
      if (DTM._tasks.length === 0) {
        DTM._taskTimer = undefined;
        return;
      }

      let task: ITask | undefined;
      while ((task = DTM._tasks.shift())) {
        switch (task.type) {
          case TASK_TYPES.PAGE_LOAD: {
            DTM._onPageLoad(task.data);
            break;
          }

          case TASK_TYPES.EVENT: {
            DTM._track(task.eventName, task.data);
            break;
          }
        }
      }

      DTM._taskTimer = undefined;
    });
  };

  private static _onPageLoad(data: Partial<IDataLayer>) {
    DTM._datalayer = {
      ...DTM._datalayer,
      ...data,
      page_url: location.href,
    };

    try {
      const previousPageURL: string = sessionStorage.getItem('currentPageUrl') || '';
      const previousPageName: string = sessionStorage.getItem('currentPageName') || '';
      const previousPageType: string = sessionStorage.getItem('currentPageType') || '';

      sessionStorage.setItem('previousPageURL', previousPageURL);
      sessionStorage.setItem('previousPageName', previousPageName);
      sessionStorage.setItem('previousPageType', previousPageType);

      sessionStorage.setItem('currentPageUrl', DTM._datalayer.page_url);
      sessionStorage.setItem('currentPageName', DTM._datalayer.pagename);
      sessionStorage.setItem('currentPageType', DTM._datalayer.pagetype);

      DTM._datalayer.previous_page = previousPageName;
      DTM._datalayer.previous_page_url = previousPageURL;
      DTM._datalayer.previous_page_type = previousPageType;

      DTM._datalayer.ui = getReactAppType();

      DTM.updateGlobalDatalayer();
      DTM._track(DTM._datalayer.eventName);
      DTM._track('global-rule');
    } catch (err) {
      //Do Nothing
    }

    if (isDev) {
      // eslint-disable-next-line no-console
      console.info('DTM Pageload == ', DTM._datalayer);
    }
  }

  private static _track(eventName: string, tags: { [key: string]: string } = {}) {
    DTM._datalayer = {
      ...DTM._datalayer,
      ...tags,
    };
    DTM.updateGlobalDatalayer();

    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('DTM Track == ', eventName, DTM._datalayer);
    }

    if (window._satellite !== undefined) {
      try {
        window._satellite.track(eventName);
      } catch (e) {
        /** DO NOTHING */
      }
    }
  }
}
